import React, { useState } from "react";
import { Row, Col, Form, Typography } from "antd";
import { CustomButton } from "../adaptors/CustomButton";
import { CustomSelect } from "../adaptors/CustomSelect";
import CustomInput from "../adaptors/CustomInput";
import { CustomLink } from "../adaptors/CustomLink";
import { variants } from "../../utils/variants";
import FormErrorMessage from "../FormErrorMessage";
import { phoneNumberValidator } from "../../utils/helpers/validations";
import "../sections/auth.scss";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import {
  cartDetailsActions,
  helperActions,
  userActions,
} from "../../utils/redux/actions";
import { UPDATE_AUTH_USER_DETAILS } from "../../../graphql/mutations/user";
import { useMutation } from "@apollo/client";
import { LMS_PLATFORM_OUT_LINK, LOGIN } from "../../utils/localization";
import { useSelector } from "react-redux";
import { LINK_CART_TO_USER } from "../../../graphql/mutations/cart";
import { EmptyMessage } from "../EmptyMessage";

const { Title, Paragraph, Text } = Typography;

const AddUserDetailsContent = (props) => {
  const { form, onAuthStateChange, handleCloseModal } = props;
  const { getFieldValue } = form;
  const user = useSelector((state) => state.user);
  const cartDetails = useSelector((state) => state.cartDetails);
  const dispatch = useDispatch();
  const { userUpdateAction, userLoginAction, userResetAction } =
    bindActionCreators(userActions, dispatch);
  const { setCartDetails } = bindActionCreators(cartDetailsActions, dispatch);
  const { openToastMessage } = bindActionCreators(helperActions, dispatch);
  const { resetCartDetails } = bindActionCreators(cartDetailsActions, dispatch);
  const [updateUserDetails] = useMutation(UPDATE_AUTH_USER_DETAILS);
  const [linkCartToUser] = useMutation(LINK_CART_TO_USER);
  const [updatingUserDetails, setUpdatingUserDetails] = useState(false);

  const initialLocationName = user?.userDetails?.location?.name;
  const initialLocationParentName = user?.userDetails?.location?.parent?.name;

  const handleUpdateUserDetails = async (
    firstName,
    lastName,
    phoneNumber,
    locationId
  ) => {
    try {
      const { data } = await updateUserDetails({
        variables: {
          firstName,
          lastName,
          mobileNumber: phoneNumber,
          locationId,
          refUrl: window.location.pathname,
        },
      });
      if (data?.userUpdate) {
        setUpdatingUserDetails(false);
        const { email, firstName, lastName, mobileNumber, location } =
          data.userUpdate;
        userUpdateAction({
          email,
          firstName,
          lastName,
          mobileNumber,
          location,
        });
        userLoginAction();
        if (cartDetails?.uuid) {
          try {
            const { data: activeCartData } = await linkCartToUser({
              variables: {
                cartUuid: cartDetails.uuid,
              },
            });
            handleCloseModal(() => {
              if (activeCartData?.linkCartToUser)
                setCartDetails(activeCartData?.linkCartToUser);
              openToastMessage({
                variant: variants.positiveToastMessage,
                title: "You are logged in!",
              });
            });
          } catch (error) {
            const gqlError = error.graphQLErrors[0];
            console.warn(gqlError);
            handleCloseModal(() => {});
          }
        }
        if (window.location.pathname == "/partners/fc-goa") {
          window.open(LMS_PLATFORM_OUT_LINK, "_blank");
        }
        window.location.reload();
      }
    } catch (error) {
      setUpdatingUserDetails(false);
      const gqlError = error.graphQLErrors[0];
      if (gqlError) {
        openToastMessage({ variant: variants.error });
      }
    }
  };

  const onFinish = (values) => {
    setUpdatingUserDetails(true);
    const { firstName, lastName, phoneNumber, location } = values;

    const selectedLocation = user?.userDetails?.locationList?.find(
      (item) => item?.value === location
    );

    const locationId = selectedLocation?.locationId;

    handleUpdateUserDetails(firstName, lastName, phoneNumber, locationId);
  };

  return (
    <Col className="authCard">
      <Title className="authCardTitle">Just a few more details</Title>
      <Paragraph className="authCardSubText">
        Your account has been created under the email
        <Text className="mailText">
          {" "}
          {user?.userDetails?.email ?? getFieldValue("email")}
        </Text>
        .
        <CustomLink
          nativeLink
          onClick={(e) => {
            e.preventDefault();
            userResetAction();
            resetCartDetails();
            onAuthStateChange(LOGIN);
          }}
          customClass="notYouLink"
        >
          Not you?
        </CustomLink>
      </Paragraph>
      <Form
        form={form}
        name="basic"
        initialValues={{
          firstName: user?.userDetails?.firstName ?? "",
          lastName: user?.userDetails?.lastName ?? "",
          phoneNumber: user?.userDetails?.mobileNumber ?? "",
          location: initialLocationName
            ? `${initialLocationName + " ," + initialLocationParentName}`
            : null,
        }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="firstName"
              rules={[
                {
                  required: true,
                  message: <FormErrorMessage />,
                },
              ]}
            >
              <CustomInput
                formItem
                label="First Name"
                variant={variants.textInput}
                placeholder="First name"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="lastName"
              rules={[
                {
                  required: true,
                  message: <FormErrorMessage />,
                },
              ]}
            >
              <CustomInput
                formItem
                label="Last Name"
                variant={variants.textInput}
                placeholder="Last Name"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="phoneNumber"
          rules={[
            {
              required: true,
              validator: phoneNumberValidator,
            },
          ]}
        >
          <CustomInput
            formItem
            label="Phone Number"
            variant={variants.numberInput}
            placeholder="Phone Number"
          />
        </Form.Item>

        <Form.Item name="location">
          <CustomSelect
            showSearch
            label="Location"
            placeholder="Select location"
            notFoundContent={<EmptyMessage messageSubInfo="No results" />}
            options={user?.userDetails?.locationList}
          />
        </Form.Item>

        <Form.Item className="submitFormItem">
          <CustomButton
            variant={variants.primaryButton}
            htmlType="submit"
            formNoValidate
            title="Continue"
            customClass="submitBtn"
            loading={updatingUserDetails}
            disabled={updatingUserDetails}
          />
        </Form.Item>
      </Form>
    </Col>
  );
};

export default AddUserDetailsContent;
